import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";

/**
 * Six Dimensions — sample scored breakdown.
 * Each card: index · title · score (X/100) · verdict tag · 4 data rows.
 * Editorial, no rounded corners, paper / line system.
 */
const dimensions = [
  {
    n: "01",
    title: "Market Opportunity Index",
    score: 88,
    verdict: "Strong",
    body:
      "Not how big the category is — how much of it is genuinely accessible to your brand at your price point in your priority market.",
    rows: [
      ["Category revenue (DE)", "€8.4M / mo"],
      ["Accessible segment", "€1.2M / mo"],
      ["New entrant success rate", "34% · 12 mo"],
      ["Irish brand presence", "0 competitors"],
    ],
  },
  {
    n: "02",
    title: "Competitor Vulnerability Map",
    score: 79,
    verdict: "Good gaps identified",
    body:
      "The exact price band, keyword cluster, and review gap your brand can occupy that top competitors have left undefended.",
    rows: [
      ["Top competitor reviews", "avg 187"],
      ["Undefended keywords", "14 found"],
      ["Price band gap", "€22 – €28"],
      ["A+ content quality", "Weak · 4 / 10"],
    ],
  },
  {
    n: "03",
    title: "Brand Readiness Score",
    score: 71,
    verdict: "Ready with conditions",
    body:
      "Seven dimensions assessed by a human operator — IP, packaging, compliance, logistics, price architecture, review foundation, founder commitment.",
    rows: [
      ["IP / Trademark", "IE only ⚠"],
      ["Packaging compliance", "Needs update"],
      ["Logistics readiness", "Dublin → DE ✓"],
      ["Review foundation", "84 UK reviews"],
    ],
  },
  {
    n: "04",
    title: "Financial Viability Score",
    score: 84,
    verdict: "Viable",
    body:
      "Net to brand — after Amazon referral, FBA, OSS VAT, PPC and Traeva fee. Conservative, base and optimistic, month by month for 12 months.",
    rows: [
      ["Amazon referral fee", "8% · beauty"],
      ["FBA fee (est.)", "€3.20 / unit"],
      ["Net margin Y1 · base", "29%"],
      ["Break-even point", "Month 4"],
    ],
  },
  {
    n: "05",
    title: "Operational Readiness",
    score: 76,
    verdict: "Ready",
    body:
      "Can the brand actually execute. Timeline realistic. Budget sufficient. Compliance work scoped. Founder bandwidth real.",
    rows: [
      ["Timeline feasibility", "10 weeks ✓"],
      ["PPC budget", "Sufficient"],
      ["OSS VAT setup", "3 weeks"],
      ["EUIPO timeline", "6 months ⚠"],
    ],
  },
  {
    n: "06",
    title: "Irish Advantage Score",
    score: 95,
    verdict: "Exceptional",
    body:
      "How much EU membership, zero import duty, OSS VAT simplicity and the Irish origin story compound into a structural edge competitors do not have.",
    rows: [
      ["Import duty advantage", "€0 vs UK €4.2k"],
      ["Irish origin premium", "High · natural cat"],
      ["OSS VAT simplicity", "3 weeks · €800"],
      ["EU story angle", "Strong"],
    ],
  },
];

function ScoreBar({ score }) {
  return (
    <div className="mt-5 h-[3px] bg-line relative overflow-hidden" aria-hidden="true">
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: `${score}%` }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        className="absolute inset-y-0 left-0 bg-coral"
      />
    </div>
  );
}

export default function BearingDimensions() {
  return (
    <section className="border-b border-line" data-testid="bearing-dimensions">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 sm:py-20 lg:py-24">
        <Reveal>
          <p className="label-mono">/ The six dimensions</p>
          <h2 className="mt-8 sm:mt-10 font-serif-i text-[36px] sm:text-[44px] lg:text-[56px] text-ink leading-[1.05] tracking-tight max-w-3xl">
            How Traeva scores your brand.
          </h2>
          <p className="mt-8 max-w-xl text-ink-soft leading-[1.7] text-[16px] sm:text-lg">
            Six dimensions, each scored 0–100 by a human operator against real category and
            competitor data — assembled for your brand, not templated.
          </p>
        </Reveal>

        <div className="mt-10 sm:mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line">
          {dimensions.map((d, i) => (
            <Reveal key={d.n} delay={i * 0.05}>
              <article
                data-testid={`dimension-${d.n}`}
                className="bg-paper p-8 sm:p-10 h-full flex flex-col"
              >
                <div className="flex items-baseline justify-between">
                  <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-coral">
                    Dimension {d.n}
                  </p>
                  <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-subtle">
                    /100
                  </p>
                </div>

                <h3 className="mt-7 font-serif-i text-[24px] sm:text-[28px] text-ink leading-tight">
                  {d.title}
                </h3>

                <div className="mt-7 flex items-baseline gap-4">
                  <p className="font-serif-i text-[56px] sm:text-[64px] text-ink leading-none">
                    {d.score}
                  </p>
                  <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-coral">
                    {d.verdict}
                  </p>
                </div>
                <ScoreBar score={d.score} />

                <p className="mt-8 text-[14px] sm:text-[15px] text-ink-muted leading-[1.7]">
                  {d.body}
                </p>

                <ul className="mt-8 border-t border-line">
                  {d.rows.map(([k, v], idx) => (
                    <li
                      key={idx}
                      className="grid grid-cols-12 gap-3 py-4 border-b border-line items-baseline"
                    >
                      <span className="col-span-7 font-mono text-[11px] tracking-[0.14em] uppercase text-ink-muted">
                        {k}
                      </span>
                      <span className="col-span-5 text-right text-[14px] sm:text-[15px] text-ink font-mono">
                        {v}
                      </span>
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
